import getBrand from "../brands/getBrand";
import { getAppConfig, getAppConfigSync, getCurrentHubId } from "./getAppConfig";

/**
 * Updates PWA configuration (manifest, title, favicon) based on current brand
 * @param {boolean} waitForConfig - If true, waits for AppConfig to load if not in cache
 */
export async function updatePWAConfig(waitForConfig = false) {
  if (waitForConfig) {
    const hubId = getCurrentHubId();
    if (hubId && !getAppConfigSync()) {
      await getAppConfig();
    }
  }
  
  const brand = getBrand();
  const user_role = localStorage.getItem('role');
  
  // Get brand string from hostname or localStorage
  const hostnameParts = window.location.hostname.split(".");
  const brandString = hostnameParts[0] || localStorage.getItem('brand') 
  
  // For super admin, always use "KOODAI" as title
  const pageTitle = user_role === 'super_admin' ? 'KOODAI' : brand.appTitle;
  if (pageTitle && document.title !== pageTitle) {
    document.title = pageTitle;
    // console.log("[updatePWAConfig] Title updated to:", pageTitle);
  }
  
  // Update or create manifest link
  let manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.setAttribute('rel', 'manifest');
    document.head.appendChild(manifestLink);
  }
  
  // Store brand in cache for service worker to use
  if ('caches' in window) {
    caches.open('smartkart-cache-v2').then((cache) => {
      cache.put('/brand-cache', new Response(JSON.stringify({ brand: brandString }), {
        headers: { 'Content-Type': 'application/json' }
      }));
    });
  }
  
  // Add cache-busting based on current hub to force browser to re-fetch manifest
  const currentHubId = getCurrentHubId();
  const cacheBuster = currentHubId
    ? `hub=${currentHubId}`
    : `t=${Date.now()}`; 
  
  // Keep manifest link pointing to manifest.json (service worker will intercept)
  // Cache-busting query param ensures browser re-fetches when hub changes
  manifestLink.setAttribute('href', `/manifest.json?${cacheBuster}`);
  // console.log("[updatePWAConfig] Manifest href set to:", `/manifest.json?${cacheBuster}`, "for hub:", currentHubId);
  
  // Update favicon (use separate favicon property if available, otherwise use appIcon)
  // Remove all existing favicon links first to avoid duplicates
  const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
  existingFavicons.forEach(link => link.remove());
  
  const faviconHref = brand.favicon || brand.appIcon || '/favicon.ico';
  
  // Create new favicon link with cache-busting for external URLs
  const faviconLink = document.createElement('link');
  faviconLink.setAttribute('rel', 'icon');
  // Add cache-busting parameter for external URLs (Azure blob storage)
  if (faviconHref.startsWith('http')) {
    const separator = faviconHref.includes('?') ? '&' : '?';
    faviconLink.setAttribute('href', `${faviconHref}${separator}v=${Date.now()}`);
  } else {
    faviconLink.setAttribute('href', faviconHref);
  }
  faviconLink.setAttribute('type', faviconHref.includes('.jpeg') || faviconHref.includes('.jpg') ? 'image/jpeg' : 'image/png');
  document.head.appendChild(faviconLink);
  // console.log("[updatePWAConfig] favicon href set to:", faviconHref);
  
  // Update apple-touch-icon
  const existingAppleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
  existingAppleIcons.forEach(link => link.remove());
  
  const appleIconHref = brand.appleTouchIcon || brand.appIcon || '/logo192.png';
  const appleTouchIcon = document.createElement('link');
  appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
  // Add cache-busting for external URLs
  if (appleIconHref.startsWith('http')) {
    const separator = appleIconHref.includes('?') ? '&' : '?';
    appleTouchIcon.setAttribute('href', `${appleIconHref}${separator}v=${Date.now()}`);
  } else {
    appleTouchIcon.setAttribute('href', appleIconHref);
  }
  document.head.appendChild(appleTouchIcon);
  
  // Update theme-color meta tag
  let themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.setAttribute('name', 'theme-color');
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.setAttribute('content', brand.theme?.primary || brand.themeColor || '#000000');
  
  // Update description meta tag
  let descriptionMeta = document.querySelector('meta[name="description"]');
  if (!descriptionMeta) {
    descriptionMeta = document.createElement('meta');
    descriptionMeta.setAttribute('name', 'description');
    document.head.appendChild(descriptionMeta);
  }
  descriptionMeta.setAttribute('content', brand.appDescription || 'SmartKart Control Hub');
}
